import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { Plus } from 'lucide-react'
import { toast } from 'sonner'

import { categoriesApi } from '@/services/categories'
import { dashboardApi } from '@/services/dashboard'
import { workersApi } from '@/services/workers'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { getServiceInstanceIdFromSearch, withServiceInstanceId } from '@/lib/service-instance'
import { cn } from '@/lib/utils'
import { DayBoard } from '@/features/scheduling/components/DayBoard'
import { SchedulePanel, type PanelActions } from '@/features/scheduling/components/SchedulePanel'
import { ScheduleStats } from '@/features/scheduling/components/ScheduleStats'
import { BRAND, describeDay, LEAVE_HATCH, OFF_HATCH, PROBLEM_RED } from '@/features/scheduling/components/schedule-styles'
import { SchedulingToolbar } from '@/features/scheduling/components/SchedulingToolbar'
import { WeekBoard } from '@/features/scheduling/components/WeekBoard'
import { useAssignWorker } from '@/features/scheduling/hooks/use-assign-worker'
import type { ScheduleWorker } from '@/features/scheduling/utils/eligibility'
import type { ScheduleJobRow } from '@/features/scheduling/utils/job-rows'
import { buildScheduleBoard, computeScheduleStats } from '@/features/scheduling/utils/schedule-board'
import { buildSevenDayWindow, toInputDate } from '@/features/scheduling/utils/schedule-data'
import type { ScheduleSelection } from '@/features/scheduling/utils/selection'
import { buildWorkerCategoryIndex } from '@/features/scheduling/utils/worker-categories'

const SCHEDULING_TABS = [
  { value: '/job/realtime', label: 'Real Time' },
  { value: '/jobs/scheduling', label: 'Schedule' },
  { value: '/job/timeline', label: 'Timeline' },
  { value: '/workdays', label: 'Work Days' },
] as const

const INPUT_DATE_RE = /^\d{4}-\d{2}-\d{2}$/

/** Workers read once per page load for category matching (same page size as the Real Time page). */
const CATEGORY_WORKERS_PAGE_SIZE = 200
/** Job categories read once to turn workers' category ids into names. */
const CATEGORIES_PAGE_SIZE = 200

const isValidInputDate = (value: string | null) => {
  if (!value || !INPUT_DATE_RE.test(value)) {
    return false
  }
  const parsed = new Date(value)
  return !Number.isNaN(parsed.getTime())
}

/** YYYY-MM-DD as a local calendar day (no UTC shift). */
const parseDayKey = (value: string) => new Date(`${value}T00:00:00`)

/** "Tue, Oct 6, 2026". */
const formatLongDayLabel = (value: string) => {
  const parsed = parseDayKey(value)
  return Number.isNaN(parsed.getTime())
    ? value
    : parsed.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })
}

/** "Oct 6 – Oct 12, 2026" (year shown once when both days share it). */
const formatRangeLabel = (startKey: string, endKey: string) => {
  const startDate = parseDayKey(startKey)
  const endDate = parseDayKey(endKey)
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
    return `${startKey} – ${endKey}`
  }
  const sameYear = startDate.getFullYear() === endDate.getFullYear()
  const startLabel = startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', ...(sameYear ? {} : { year: 'numeric' }) })
  const endLabel = endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  return `${startLabel} – ${endLabel}`
}

const LegendSwatch = ({ style, label }: { style: React.CSSProperties; label: string }) => (
  <span className="inline-flex items-center gap-1.5">
    <span className="h-3 w-3 rounded" style={style} aria-hidden="true" />
    {label}
  </span>
)

export default function ScheduleWorkersPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const serviceInstanceId = getServiceInstanceIdFromSearch(location.search)
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search])

  const urlStart = searchParams.get('start')
  const urlEnd = searchParams.get('end')
  const urlView = searchParams.get('view')
  const urlDay = searchParams.get('day')

  const today = useMemo(() => toInputDate(new Date()), [])
  const defaultEnd = useMemo(() => {
    const value = new Date()
    value.setDate(value.getDate() + 6)
    return toInputDate(value)
  }, [])

  const activeSchedulingTab = useMemo(() => {
    const pathname = location.pathname
    if (pathname.startsWith('/job/realtime')) {
      return '/job/realtime'
    }
    if (pathname.startsWith('/job/timeline') || pathname.startsWith('/timeline')) {
      return '/job/timeline'
    }
    if (pathname.startsWith('/workdays')) {
      return '/workdays'
    }
    return '/jobs/scheduling'
  }, [location.pathname])

  const handleSchedulingTabNavigate = (nextPath: string) => {
    if (!nextPath || nextPath === activeSchedulingTab) {
      return
    }

    navigate(withServiceInstanceId(nextPath, serviceInstanceId))
  }

  const [start, setStart] = useState(() => (isValidInputDate(urlStart) ? (urlStart as string) : today))
  const [end, setEnd] = useState(() => (isValidInputDate(urlEnd) ? (urlEnd as string) : defaultEnd))
  const [viewMode, setViewMode] = useState<'week' | 'day'>(() => (urlView === 'day' ? 'day' : 'week'))
  const [locationFilter, setLocationFilter] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('')
  /** Day in focus: the Day view's day and the highlighted column in the week. */
  const [selectedDate, setSelectedDate] = useState(() => (isValidInputDate(urlDay) ? (urlDay as string) : start))
  const [selection, setSelection] = useState<ScheduleSelection>(null)
  const [note, setNote] = useState<string | null>(null)

  const shiftDate = (value: string, days: number) => {
    const parsed = new Date(value)
    if (Number.isNaN(parsed.getTime())) {
      return value
    }
    parsed.setDate(parsed.getDate() + days)
    return toInputDate(parsed)
  }

  const handleMoveDateRange = (days: number) => {
    setStart((prev) => shiftDate(prev, days))
    setEnd((prev) => shiftDate(prev, days))
  }

  const handleToday = () => {
    setStart(today)
    setEnd(shiftDate(today, 6))
    setSelectedDate(today)
  }

  /** Same as the previous date picker: the range starts on the picked day and keeps its length. */
  const handlePickDate = (nextStart: string) => {
    const current = new Date(start)
    const next = new Date(nextStart)
    setSelectedDate(nextStart)
    if (Number.isNaN(current.getTime()) || Number.isNaN(next.getTime())) {
      setStart(nextStart)
      return
    }
    const diffMs = next.getTime() - current.getTime()
    const diffDays = Math.round(diffMs / (24 * 60 * 60 * 1000))
    setStart(nextStart)
    setEnd((prev) => shiftDate(prev, diffDays))
  }

  const queryKey = useMemo(() => ['job-schedule-workers-overview', start, end], [start, end])
  const { data, isLoading, isError, isFetching, isPlaceholderData } = useQuery({
    queryKey,
    queryFn: () => dashboardApi.getScheduleWorkersOverview(start, end),
    placeholderData: keepPreviousData,
  })

  // Category matching only: never blocks the page; on failure the category rule is skipped.
  const { data: categoryWorkers, isError: categoryWorkersFailed } = useQuery({
    queryKey: ['scheduling-category-workers', serviceInstanceId],
    queryFn: ({ signal }) => workersApi.getWorkers({ page_size: CATEGORY_WORKERS_PAGE_SIZE }, { signal }).then((page) => page?.results ?? []),
    staleTime: Infinity,
    retry: false,
    refetchOnWindowFocus: false,
  })
  const { data: jobCategories } = useQuery({
    queryKey: ['scheduling-job-categories', serviceInstanceId],
    queryFn: () => categoriesApi.getCategories({ page_size: CATEGORIES_PAGE_SIZE }).then((page) => page?.results ?? []),
    staleTime: Infinity,
    retry: false,
    refetchOnWindowFocus: false,
  })
  const categoryState = categoryWorkers ? 'ready' : categoryWorkersFailed ? 'failed' : 'loading'
  const categoryIndex = useMemo(() => (categoryWorkers ? buildWorkerCategoryIndex(categoryWorkers) : null), [categoryWorkers])
  const categoryNameById = useMemo(
    () => new Map((jobCategories ?? []).map((category) => [String(category.id), String(category.name || '').trim()])),
    [jobCategories],
  )

  const schedules = useMemo(() => data?.upcoming_workday_schedules || [], [data])
  const availabilityItems = useMemo(() => data?.worker_day_availability || [], [data])
  const leaveItems = useMemo(() => data?.worker_day_leaves || [], [data])
  const unassignedAssignments = useMemo(() => data?.unassigned_assignments || [], [data])

  const locationOptions = useMemo(
    () => {
      const values = schedules.map((entry) => {
        const raw = String(entry.location_name || '').trim()
        return raw || 'Unknown location'
      })
      return Array.from(new Set(values)).sort((a, b) => a.localeCompare(b))
    },
    [schedules],
  )

  const categoryOptions = useMemo(
    () => {
      const values = schedules.map((entry) => {
        const raw = String(entry.category_name || '').trim()
        return raw || 'Unknown category'
      })
      return Array.from(new Set(values)).sort((a, b) => a.localeCompare(b))
    },
    [schedules],
  )

  const sevenDays = useMemo(() => buildSevenDayWindow(start), [start])
  const focusDay = sevenDays.includes(selectedDate) ? selectedDate : (sevenDays[0] ?? start)

  const board = useMemo(() => buildScheduleBoard({
    days: sevenDays,
    schedules,
    unassignedAssignments,
    availabilityItems,
    leaveItems,
    categoryIndex,
    categoryNameById,
    locationFilter,
    categoryFilter,
  }), [availabilityItems, categoryFilter, categoryIndex, categoryNameById, leaveItems, locationFilter, schedules, sevenDays, unassignedAssignments])

  const statsDay = sevenDays.includes(today) ? today : focusDay
  const stats = useMemo(() => computeScheduleStats(board, statsDay), [board, statsDay])
  const onShiftLabel = statsDay === today ? 'On shift today' : `On shift ${describeDay(statsDay).short}`

  const { assign, remove, pendingRowKeys } = useAssignWorker(queryKey)

  const jobWhen = (job: ScheduleJobRow) => {
    const info = describeDay(job.date)
    return `${info.dow} ${info.num}${job.startMinute == null ? '' : ` ${job.timeLabel.split(' - ')[0]}`}`
  }

  const actions = useMemo<PanelActions>(() => {
    const toRowWorker = (worker: ScheduleWorker) => ({ workerId: worker.id, name: worker.name })
    // Completed jobs are read only (the backend refuses too).
    const refuseCompleted = (job: ScheduleJobRow) => {
      if (job.isCompleted) toast.error(`${job.title} is completed`, { description: 'Completed jobs are read only, so no worker can be assigned or removed.' })
      return job.isCompleted
    }
    // Same rule as the grid / panel, checked again right before saving.
    const guard = (job: ScheduleJobRow, worker: ScheduleWorker) => {
      if (refuseCompleted(job)) return false
      const verdict = board.check(worker, job)
      if (!verdict.ok) {
        toast.error(`${worker.name} can't take ${job.title}`, { description: verdict.text })
      }
      return verdict.ok
    }
    return {
      onAssign: async (job, worker) => {
        if (!guard(job, worker)) return false
        const ok = await assign(job, toRowWorker(worker))
        if (ok) setNote(`${job.title} (${jobWhen(job)}) assigned to ${worker.name}.`)
        return ok
      },
      onReplace: async (job, worker, previous, assignmentId) => {
        if (!guard(job, worker)) return false
        const added = await assign(job, toRowWorker(worker))
        if (!added) return false
        const removed = await remove(job, { ...toRowWorker(previous), assignmentId })
        setNote(removed
          ? `${job.title} (${jobWhen(job)}): ${previous.name} replaced by ${worker.name}.`
          : `${worker.name} added to ${job.title} (${jobWhen(job)}); ${previous.name} is still on it.`)
        return removed
      },
      onRemove: async (job, worker, assignmentId) => {
        if (refuseCompleted(job)) return false
        const ok = await remove(job, { ...toRowWorker(worker), assignmentId })
        if (ok) setNote(`${worker.name} removed from ${job.title} (${jobWhen(job)}).`)
        return ok
      },
    }
  }, [assign, board, remove])

  const handlePickJob = useCallback((job: ScheduleJobRow) => {
    setSelection({ type: 'job', key: job.key })
    setSelectedDate(job.date)
    setNote(null)
  }, [])

  const handlePickWorker = useCallback((worker: ScheduleWorker) => {
    setSelection({ type: 'worker', id: worker.id })
    setNote(null)
  }, [])

  const handleClearSelection = () => {
    setSelection(null)
    setNote(null)
  }

  const handleOpenDay = useCallback((day: string) => {
    setSelectedDate(day)
    setViewMode('day')
  }, [])

  /** The selection and focus day are kept when switching views. */
  const handleViewModeChange = (mode: 'week' | 'day') => {
    setSelectedDate(focusDay)
    setViewMode(mode)
  }

  /** Week: previous / next week. Day: previous / next day, moving the week when it leaves it. */
  const handleMove = (direction: -1 | 1) => {
    if (viewMode === 'week') {
      handleMoveDateRange(direction * 7)
      setSelectedDate(shiftDate(focusDay, direction * 7))
      return
    }
    const nextDay = shiftDate(focusDay, direction)
    if (!sevenDays.includes(nextDay)) {
      handleMoveDateRange(direction * 7)
    }
    setSelectedDate(nextDay)
  }

  const rangeLabel = viewMode === 'day' ? formatLongDayLabel(focusDay) : formatRangeLabel(sevenDays[0] ?? start, sevenDays[6] ?? end)

  useEffect(() => {
    const nextStart = isValidInputDate(urlStart) ? (urlStart as string) : today
    const nextEnd = isValidInputDate(urlEnd) ? (urlEnd as string) : defaultEnd
    const nextView: 'week' | 'day' = urlView === 'day' ? 'day' : 'week'

    setStart((prev) => (prev === nextStart ? prev : nextStart))
    setEnd((prev) => (prev === nextEnd ? prev : nextEnd))
    setViewMode((prev) => (prev === nextView ? prev : nextView))
  }, [defaultEnd, today, urlEnd, urlStart, urlView])

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    params.set('view', viewMode)
    params.set('start', start)
    params.set('end', end)
    if (viewMode === 'day') {
      params.set('day', focusDay)
    } else {
      params.delete('day')
    }

    const nextSearch = `?${params.toString()}`
    if (nextSearch === location.search) {
      return
    }

    navigate(
      {
        pathname: location.pathname,
        search: nextSearch,
      },
      { replace: true },
    )
  }, [end, focusDay, location.pathname, location.search, navigate, start, viewMode])

  if (isLoading) {
    return (
      <div className="space-y-4 p-4 sm:p-6">
        <Skeleton className="h-8 w-64" />
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton key={index} className="h-24 w-full rounded-xl" />
          ))}
        </div>
        <Skeleton className="h-96 w-full rounded-xl" />
      </div>
    )
  }

  if (isError || !data) {
    return (
      <div className="space-y-4 p-4 sm:p-6">
        <h1 className="text-2xl font-semibold">Schedule workers</h1>
        <Card>
          <CardContent className="p-6 text-sm text-muted-foreground">
            Unable to load schedule workers snapshot.
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-4 bg-[#F3F5F1] p-4 text-[#14231B] sm:p-6">
      <nav className="flex flex-wrap gap-1 self-start rounded-xl border border-[#DDE4DC] bg-white p-1 sm:inline-flex" aria-label="Scheduling sections">
        {SCHEDULING_TABS.map((tab) => {
          const isActive = activeSchedulingTab === tab.value
          return (
            <button
              key={tab.value}
              type="button"
              onClick={() => handleSchedulingTabNavigate(tab.value)}
              aria-current={isActive ? 'page' : undefined}
              className={cn(
                'min-h-11 rounded-[9px] px-4 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#14231B]',
                isActive ? 'font-semibold text-white' : 'font-medium text-[#4A5A51] hover:bg-[#F4F9F5]',
              )}
              style={isActive ? { background: BRAND } : undefined}
            >
              {tab.label}
            </button>
          )
        })}
      </nav>

      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-[34px] font-bold leading-tight tracking-[-0.5px]">Schedule workers</h1>
          <p className="mt-1.5 text-[15px] text-[#4A5A51]">Check who is free, on leave or clashing, then assign the right worker.</p>
        </div>
        <button
          type="button"
          onClick={() => navigate(withServiceInstanceId('/job/create', serviceInstanceId))}
          className="inline-flex min-h-12 items-center gap-2 rounded-[10px] px-5 text-[15px] font-semibold text-white hover:brightness-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#14231B] focus-visible:ring-offset-2"
          style={{ background: BRAND }}
        >
          <Plus className="h-4 w-4" aria-hidden="true" />
          New job
        </button>
      </div>

      <SchedulingToolbar
        rangeLabel={rangeLabel}
        viewMode={viewMode}
        pickedDate={start}
        isFetching={isFetching}
        locationFilter={locationFilter}
        categoryFilter={categoryFilter}
        locationOptions={locationOptions}
        categoryOptions={categoryOptions}
        onViewModeChange={handleViewModeChange}
        onMove={handleMove}
        onToday={handleToday}
        onPickDate={handlePickDate}
        onLocationFilterChange={setLocationFilter}
        onCategoryFilterChange={setCategoryFilter}
      />

      <ScheduleStats stats={stats} onShiftLabel={onShiftLabel} />

      <div className="grid items-start gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(340px,400px)]">
        <section
          className={cn('min-w-0 overflow-hidden rounded-2xl border border-[#DDE4DC] bg-white transition-opacity', isPlaceholderData && 'opacity-60')}
          aria-busy={isPlaceholderData}
          aria-label={viewMode === 'week' ? 'Week schedule' : 'Day timeline'}
        >
          <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 px-5 pb-3 pt-4">
            <h2 className="text-[17px] font-bold">{viewMode === 'week' ? 'Week at a glance' : `Day timeline · ${describeDay(focusDay).long}`}</h2>
            <p className="text-[13px] text-[#4A5A51]">
              {viewMode === 'week'
                ? 'Click a job to find free workers. Click a worker for jobs that fit them. Click a day to open it.'
                : 'Green band = shift. Click a job to find free workers, or a worker for jobs that fit them.'}
            </p>
          </div>

          {viewMode === 'week' ? (
            <div className="overflow-x-auto">
              <WeekBoard
                board={board}
                selection={selection}
                selectedDay={focusDay}
                today={today}
                onPickJob={handlePickJob}
                onPickWorker={handlePickWorker}
                onOpenDay={handleOpenDay}
              />
            </div>
          ) : (
            <DayBoard
              board={board}
              day={focusDay}
              selection={selection}
              onPickDay={setSelectedDate}
              onPickJob={handlePickJob}
              onPickWorker={handlePickWorker}
            />
          )}

          <div className="flex flex-wrap items-center gap-x-[18px] gap-y-2 border-t border-[#E6ECE5] px-5 py-3 text-[13px] text-[#3C4B43]">
            <LegendSwatch style={LEAVE_HATCH} label="On leave" />
            <LegendSwatch style={OFF_HATCH} label="Day off" />
            <LegendSwatch style={{ background: '#FFFFFF', border: `2px solid ${PROBLEM_RED}`, boxSizing: 'border-box' }} label="Problem with this assignment" />
          </div>
        </section>

        <SchedulePanel
          board={board}
          selection={selection}
          view={viewMode}
          selectedDay={focusDay}
          note={note}
          pendingRowKeys={pendingRowKeys}
          categoryState={categoryState}
          serviceInstanceId={serviceInstanceId}
          actions={actions}
          onPickJob={handlePickJob}
          onClear={handleClearSelection}
        />
      </div>
    </div>
  )
}
